import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faTag } from '@fortawesome/pro-regular-svg-icons';
import { Typography } from '@design-system/components/typography/typography';
import {
  BundleCard,
  BundleCardFreeTrial,
} from '../viewModel/subscriptionBundle.vm.mapper';
import { Tooltip } from '@mui/material';
import { Checkbox } from '@design-system/components/checkbox/checkbox';
import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { sprinkles } from '@design-system/sprinkles.css';

export const BundleDetailsCard = ({
  card,
  editMode,
}: {
  card: BundleCard;
  editMode: boolean;
}) => (
  <div className="flex flex-col gap-2 bg-white p-4 rounded-[10px] shadow-exercise">
    <div className="flex items-center gap-4">
      <Typography variant="headline" size="medium">
        {card.name}
      </Typography>
      {!card.tooltipText ? null : (
        <Tooltip
          title={
            <div className="flex flex-col gap-4">
              {card.tooltipText.map((text) => (
                <Typography key={text}>{text}</Typography>
              ))}
            </div>
          }
          placement="right-start"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'black',
                color: 'white',
                fontSize: '12px',
                fontWeight: 500,
                fontFamily: 'inherit',
                padding: '4px 8px',
              },
            },
          }}
        >
          <FontAwesomeIcon icon={faInfoCircle} />
        </Tooltip>
      )}
    </div>
    <div className="flex gap-2 items-center">
      <FontAwesomeIcon icon={faTag} />
      <Typography variant="body" size="large" fontWeight="normal">
        {card.price}
      </Typography>
    </div>
    <FreeTrial
      freeTrial={card.freeTrial}
      editMode={editMode}
      showFreeTrial={card.showFreeTrial}
    />
  </div>
);

const FreeTrial = ({
  freeTrial,
  showFreeTrial,
  editMode,
}: {
  freeTrial?: BundleCardFreeTrial;
  showFreeTrial: boolean;
  editMode: boolean;
}) => {
  if (!freeTrial || !showFreeTrial) return null;

  return (
    <div className="flex gap-2 items-center">
      <Typography variant="body" size="large" fontWeight="normal">
        7-day free trial for:
      </Typography>
      {!editMode ? (
        <Typography variant="body" size="large" fontWeight="normal">
          <FreeTrialLabels freeTrial={freeTrial} />
        </Typography>
      ) : (
        <div className="flex gap-4">
          <Checkbox
            label="monthly"
            checkboxVariant="neutral"
            defaultChecked={freeTrial.monthly.checked}
            checked={freeTrial.monthly.checked}
            onChange={freeTrial.monthly.onChange}
            textVariant="body"
            textSize="large"
            textFontWeight="medium"
          />
          <Checkbox
            label="annual"
            checkboxVariant="neutral"
            defaultChecked={freeTrial.annual.checked}
            checked={freeTrial.annual.checked}
            onChange={freeTrial.annual.onChange}
            textVariant="body"
            textSize="large"
            textFontWeight="medium"
          />
        </div>
      )}
    </div>
  );
};

const FreeTrialLabels = ({
  freeTrial,
}: {
  freeTrial?: BundleCardFreeTrial;
}) => {
  if (!freeTrial) return null;

  const labels = [
    {
      title: 'monthly',
      value: freeTrial.monthly.checked,
    },
    {
      title: 'annual',
      value: freeTrial.annual.checked,
    },
  ];

  return (
    <div className="flex gap-4 items-center">
      {labels.map(({ title, value }) => (
        <div className="flex gap-2 items-center" key={title}>
          <FontAwesomeIcon
            icon={value ? faCheck : faXmark}
            className={sprinkles({
              color: value ? 'B-100' : 'N-40',
            })}
          />
          <Typography
            variant="body"
            size="large"
            fontWeight="normal"
            className={sprinkles({
              color: value ? 'B-100' : 'N-40',
            })}
          >
            {title}
          </Typography>
        </div>
      ))}
    </div>
  );
};
